import {useQuasar} from 'quasar';
import AlertDialog from '@/vue/molecules/alert-dialog.vue';
import {useAlertConfirmationStore} from '@/ts/store/alert-confirmation-store';

interface DialogOptions {
  headline?: string;
  cancelLabel?: string;
  okLabel?: string;
}

interface UseWarningInterface {
  showConfirmDialog(message: string, options?: DialogOptions, onceId?: string): Promise<void>
  showAlertDialog(message: string, options?: DialogOptions): Promise<void>
}

/** Provides confirm and alert dialog helpers backed by Quasar dialogs, with optional one-time confirmation support */
export function useAlert(): UseWarningInterface {
  const $q = useQuasar();
  const {checkWhetherAlertIsAlreadyConfirmed, confirmAlert} = useAlertConfirmationStore();

  async function showConfirmDialog(message: string, options?: DialogOptions, onceId?: string): Promise<void> {
    const isAlertIsAlreadyConfirmed = onceId ? await checkWhetherAlertIsAlreadyConfirmed(onceId) : false;
    if (isAlertIsAlreadyConfirmed) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject): void => {
      $q.dialog({
        component: AlertDialog,
        componentProps: {
          message,
          cancelBtnDisplayed: true,
          headline: options?.headline,
          cancelLabel: options?.cancelLabel,
          okLabel: options?.okLabel,
        },
      }).onOk(() => {
        if (onceId) {
          void confirmAlert(onceId);
        }
        resolve();
      }).onCancel(reject);
    });
  }

  async function showAlertDialog(message: string, options?: DialogOptions): Promise<void> {
    return new Promise((resolve): void => {
      $q.dialog({
        component: AlertDialog,
        componentProps: {
          message,
          cancelBtnDisplayed: false,
          headline: options?.headline,
          cancelLabel: options?.cancelLabel,
          okLabel: options?.okLabel,
        },
      }).onOk(resolve);
    });
  }

  return {
    showConfirmDialog,
    showAlertDialog,
  };
}